import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { addDays } from 'date-fns'

import { getRandomItems, shuffleArray } from '../../common/utils/commonUtils'
import { useEssentialsFetcher } from '../../essentials/hooks/useEssentialsFetcher'
import { useGetEssentialsArticlesByCategory } from '../../essentials/hooks/useGetEssentialsArticlesByCategory'
import { useGetEssentialsCourses } from '../../essentials/hooks/useGetEssentialsCourses'
import { ESSENTIALS_GET_ARTICLES_QUERY } from '../../essentials/queries'
import {
  ESSENTIALS_CATEGORY,
  ESSENTIALS_COURSE,
  ESSENTIALS_SUBCATEGORY,
  EssentialsArticleRaw,
  EssentialsContentFormatted,
  EssentialsContentRaw,
  EssentialsCourseRaw,
} from '../../essentials/types'
import {
  formatEssentialsArticle,
  formatEssentialsCourse,
  isEssentialsUriCoursesCategory,
  mergeEssentialsContents,
} from '../utils'

type Params = {
  essentialsCmsUrl: string
  essentialsCategories?: ESSENTIALS_CATEGORY[]
  essentialsExcludedSubcategories?: ESSENTIALS_SUBCATEGORY[]
  essentialsCourses?: ESSENTIALS_COURSE[]
  essentialsCoursesPositions?: Partial<Record<ESSENTIALS_COURSE, number>>
  essentialsArticles?: number[]
  userDisplayLanguage: string
  cacheKey?: string
  minNumberOfContents?: number
  shouldRandomizeOrder?: boolean
  shouldUseCache?: boolean
  defaultMixpanelProps?:
    | {
        [key: string]: any
      }
    | undefined
  trackClickEssentialsContent: (additionalMixpanelProps: { [key: string]: any }) => void
  onNavigateToEssentialsContent: ({
    subPath,
    title,
    locale,
  }: {
    subPath: string
    title: string
    locale: string
  }) => void
  contentsStringFromStorage?: string | null
  saveToStorage?: (data: { contents: EssentialsContentRaw[]; expirationTimestamp: string }, updateRef?: boolean) => void
  logError?: (error: Error) => void
  skipFetch?: boolean
  maxNumberOfContents?: number
  articleMetadata?: Record<number, { sectionTitle: string }>
}

/**
 * Fetches essentials contents:
 * - Will fetch articles by id defined by `essentialsArticles` and return all the articles
 * - Will fetch courses defined by `essentialsCourses` and return all the courses
 * - Will fetch random articles from categories defined by `essentialsCategories`, up until we have enough articles to satisfy `minNumberOfContents` (eg if minNumberOfContents is 3 and we have 1 course from `essentialsCourses` and 1 article from `essentialsArticles`, we will return 1 random article)
 *     - No articles will be fetched from `essentialsExcludedSubcategories`
 * - Results will be shuffled if `shouldRandomize` is true
 * - If `essentialsCoursesPositions` is provided, courses will be returned in the specified order, with articles filling the remaining spaces in order of their position in `essentialsArticles`
 * - If `shouldUseCache` is true, contents will be cached by `cacheKey` for 1 day
 *
 * If this hook keeps rerunning, make sure everything is memoized.
 */
export const useGetEssentialsContentCollection = ({
  essentialsCmsUrl,
  essentialsCategories,
  essentialsExcludedSubcategories,
  essentialsCourses,
  essentialsCoursesPositions,
  essentialsArticles,
  userDisplayLanguage,
  minNumberOfContents = 3,
  shouldRandomizeOrder = false,
  shouldUseCache = false,
  cacheKey,
  defaultMixpanelProps,
  trackClickEssentialsContent,
  onNavigateToEssentialsContent,
  contentsStringFromStorage,
  saveToStorage,
  logError,
  skipFetch,
  maxNumberOfContents,
  articleMetadata,
}: Params) => {
  const hasSetFinalContentsList = useRef(false)
  const [finalContentsList, setFinalContentsList] = useState<EssentialsContentFormatted[]>([])

  const getCachedEssentialsContents = useCallback(() => {
    if (!shouldUseCache || !cacheKey || !saveToStorage) {
      return null
    }
    const essentialsData = JSON.parse(contentsStringFromStorage || '{}')

    const cachedEssentials = essentialsData[cacheKey]
    if (cachedEssentials) {
      if (Date.now() > cachedEssentials.expirationTimestamp) {
        delete essentialsData[cacheKey]
        saveToStorage(essentialsData, true)
        return null
      }
      return cachedEssentials.contents
    }
    return null
  }, [cacheKey, contentsStringFromStorage, saveToStorage, shouldUseCache])

  const saveEssentialsContents = useCallback(
    (contents: EssentialsContentRaw[]) => {
      if (!shouldUseCache || !cacheKey || !saveToStorage) {
        return
      }
      const cachedEssentials = JSON.parse(contentsStringFromStorage || '{}')
      cachedEssentials[cacheKey] = {
        contents,
        expirationTimestamp: addDays(new Date(), 1).getTime(),
      }
      if (saveToStorage) {
        saveToStorage(cachedEssentials)
      }
    },
    [cacheKey, contentsStringFromStorage, saveToStorage, shouldUseCache],
  )

  const cachedContents = useMemo(() => {
    return shouldUseCache ? getCachedEssentialsContents() : null
  }, [getCachedEssentialsContents, shouldUseCache])

  const shouldSkipFetchContents = skipFetch || (cachedContents && cachedContents.length)

  const { essentialsArticleRawData: essentialsArticlesDataById, onEssentialsCardPress } = useEssentialsFetcher({
    essentialsCmsUrl,
    articleIds: essentialsArticles ?? [],
    graphQuery: ESSENTIALS_GET_ARTICLES_QUERY,
    userDisplayLanguage,
    trackEvent: trackClickEssentialsContent,
    navigate: onNavigateToEssentialsContent,
    defaultMixpanelProps,
    skipFetch: shouldSkipFetchContents,
    articleMetadata,
  })

  const essentialsArticlesDataByCategory = useGetEssentialsArticlesByCategory({
    essentialsCmsUrl,
    userDisplayLanguage,
    essentialsCategories,
    essentialsExcludedSubcategories,
    logError,
    skipFetch: shouldSkipFetchContents,
  })

  const essentialsCourseData = useGetEssentialsCourses({
    essentialsCmsUrl,
    userDisplayLanguage,
    essentialsCourses,
    logError,
    skipFetch: shouldSkipFetchContents,
  })

  const formatContent = useCallback(
    (contentList: EssentialsContentRaw[]) => {
      return contentList.map((rawContent) => {
        if (isEssentialsUriCoursesCategory(rawContent.uri)) {
          return formatEssentialsCourse(rawContent as EssentialsCourseRaw, onEssentialsCardPress, userDisplayLanguage)
        } else {
          return formatEssentialsArticle(rawContent as EssentialsArticleRaw, onEssentialsCardPress, userDisplayLanguage)
        }
      })
    },
    [onEssentialsCardPress, userDisplayLanguage],
  )

  const setResult = (list: EssentialsContentFormatted[]) => {
    setFinalContentsList(list)
    hasSetFinalContentsList.current = true
  }

  useEffect(() => {
    if (skipFetch || hasSetFinalContentsList.current) {
      return
    }

    if (cachedContents && cachedContents.length) {
      setResult(formatContent(cachedContents))
      return
    }

    if (
      (essentialsArticles?.length && !essentialsArticlesDataById.length) ||
      (essentialsCourses?.length && !essentialsCourseData.length) ||
      (essentialsCategories?.length && !essentialsArticlesDataByCategory.length)
    ) {
      return
    }

    let combinedArticlesList: EssentialsArticleRaw[] = [...essentialsArticlesDataById]
    const numArticlesToUseFromCategories = Math.max(
      0,
      minNumberOfContents - essentialsCourseData.length - essentialsArticlesDataById.length,
    )
    if (numArticlesToUseFromCategories > 0) {
      combinedArticlesList = [
        ...combinedArticlesList,
        ...getRandomItems(essentialsArticlesDataByCategory, numArticlesToUseFromCategories),
      ]
    }

    let finalContentList: EssentialsContentRaw[] = [...combinedArticlesList, ...essentialsCourseData]
    if (shouldRandomizeOrder) {
      finalContentList = shuffleArray(finalContentList)
    } else if (essentialsCoursesPositions) {
      finalContentList = mergeEssentialsContents(combinedArticlesList, essentialsCourseData, essentialsCoursesPositions)
    }

    if (maxNumberOfContents && maxNumberOfContents < finalContentList.length) {
      finalContentList = finalContentList.splice(0, maxNumberOfContents)
    }

    saveEssentialsContents(finalContentList)
    setResult(formatContent(finalContentList))
  }, [
    cachedContents,
    essentialsArticles?.length,
    essentialsArticlesDataByCategory,
    essentialsArticlesDataById,
    essentialsCategories?.length,
    essentialsCourseData,
    essentialsCourses?.length,
    essentialsCoursesPositions,
    formatContent,
    maxNumberOfContents,
    minNumberOfContents,
    saveEssentialsContents,
    shouldRandomizeOrder,
    skipFetch,
  ])

  return finalContentsList
}
